/**
 * Dictation Slice — JS-recording dictation state management.
 *
 * Drives the in-app dictation bubble for the web JS-recording flow:
 * start → record → transcribe → insert. Used on both web and desktop —
 * desktop's in-app dictate-button click routes through this slice (not
 * the native Rust path).
 *
 * Desktop's GLOBAL-hotkey dictation does NOT use this slice — Tauri
 * events flow directly into `useWidgetDictationEvents`, which owns the
 * widget UI + analytics for that path.
 *
 * ## Dictation Modes
 *
 * - **HOLD**: Push-to-talk. Hold key to record, release to stop.
 * - **HANDSFREE**: Toggle mode. Double-tap to start, tap again to stop.
 *
 * ## Key Functions
 *
 * - `start()`: Checks mic permissions, captures cursor position, starts JS recording
 * - `stop()`: Waits for transcription, post-processes, inserts via DOM
 * - `cancel()`: Aborts recording without inserting
 * - `reset()`: Returns to INACTIVE state
 *
 * ## Concurrency Guards
 *
 * - `status === 'LISTENING'` check at start of stop() prevents concurrent calls
 * - Selection state captured at start AND stop to handle cursor movement
 */

import {
  checkMicPermissionAndDevices,
  DeviceInUseError,
  MicPermissionError,
  NoMicDevicesError,
} from '@heidi/capture/useSessionRecordingStore/lib/checkMicPermissionAndDevices';
import { isTauri } from '@heidi/runtime/platform';
import {
  ensureCurrentUserSnippets,
  getCurrentUser,
  readDefaultDictationInputLanguages,
} from '@heidi/workspace/data';
import { getDefaultStore } from 'jotai';
import pRetry from 'p-retry';
import { type StateCreator } from 'zustand';
import {
  type ErrorCode,
  type MessageLevel,
} from '../../../contracts/dictation-error.types';
import { type DictationTriggeredBy } from '../../../contracts/dictation-session-analytics.types';
import { DICTATION_LOGS_INFINITE_KEY } from '../../../contracts/logs-query-keys';
import {
  trackDictationSessionEnd,
  usesKeyboardChord,
} from '../../../data/analytics/dictationAnalytics';
import { trackDictationCsatDismissed } from '../../../data/analytics/trackDictationCsatAnalytics';
import {
  trackDictationCompleted,
  trackDictationStarted,
} from '../../../data/analytics/trackDictationSessionAnalytics';
import { getDictationDataConfig } from '../../../data/config';
import { invalidateDictationLogsStats } from '../../../data/invalidateDictationLogsStats';
import { armDictateReengagementCard } from '../../../data/reengagement/general-card/atoms';
import { isDictateUserFeedbackEnabled } from '../../../flags';
import {
  dictationFeedbackPromptCooldownAtom,
  dictationFeedbackPromptIncrementAtom,
} from '../../feedback/dictation-feedback-cadence';
import { getDictationBubblePorts } from '../bubble-ports';
import {
  dictationCsatDismissAtom,
  dictationCsatIncrementAtom,
  dictationCsatPopoverStateAtom,
} from '../csat/dictationCsatAtoms';
import { buildUserContext } from './buildUserContext';
import { ensureCurrentCustomVocabulary } from './customVocabularyCache';
import { describeTranscriptionFailure } from './describeTranscriptionFailure';
import { dictationLogger } from './dictationLogger';
import {
  readDictationContextFromCache,
  resolveDictationContext,
} from './getDictationContext';
import {
  captureSelectionState,
  DictationInsertionError,
  insertText,
  type SelectionState,
} from './insertText';
import { type ProcessedResult, transcribeChunk } from './transcribeChunk';
import { type DictationSlice, type DictationStore } from './types';

const MIN_RECORDING_DURATION_MS = 1000;

/** Both logs invalidations over the app singleton, for callers outside React. */
const invalidateDictationLogs = (): void => {
  const queryClient = getDictationDataConfig().getQueryClient?.();
  if (!queryClient) {
    throw new Error(
      '[dictationBubble/store] query client not wired — configureDictationData must supply getQueryClient'
    );
  }
  void queryClient.invalidateQueries({
    queryKey: [DICTATION_LOGS_INFINITE_KEY],
  });
  invalidateDictationLogsStats(queryClient);
};

/**
 * Resolve the analytics `triggered_by` value for the current dictation lifecycle.
 *
 * Platform-agnostic — Amplitude auto-attaches `os_name` and `client_platform`
 * as separate properties, so we don't double-encode them here.
 *
 * Source takes precedence: button-triggered dictation gets the button variant
 * regardless of mode (the button sets mode=HANDSFREE for UI but the trigger
 * was still a click). Otherwise falls back to mode-based hotkey variants.
 * Defaults to `hotkey_hold` when both source is null and mode is null.
 */
const resolveTriggeredBy = (
  dictationMode: DictationStore['dictationMode'],
  source: DictationStore['source']
): DictationTriggeredBy => {
  // 'button_click_evidence' is the analytics taxonomy value — do not rename.
  if (source === 'webDictateButton') return 'button_click_evidence';
  return dictationMode === 'HANDSFREE' ? 'hotkey_hands_free' : 'hotkey_hold';
};

/**
 * '+'-joined chord for the `hotkey` Amplitude property. Null on every trigger
 * that pressed no chord. Reads `getDictationHotkey()` which is layout-aware
 * via the cached `keyboardLayoutAtom`.
 */
const formatHotkeyForAnalytics = (triggeredBy: DictationTriggeredBy): string | null =>
  usesKeyboardChord(triggeredBy)
    ? getDictationBubblePorts().getDictationHotkey().join('+')
    : null;

const computeOutputLatencyMs = (recordingEndedAt: number | null): number | null =>
  recordingEndedAt === null ? null : Date.now() - recordingEndedAt;

const initialState = {
  status: 'INACTIVE',
  text: '',
  startedAt: null,
  messageLevel: null,
  errorCode: null,
  persistent: false,
  dictationMode: null,
  editor: null,
  source: null,
  insertionNodes: {
    start: null,
    stop: null,
  },
  // SCR-3855: id of the start() call currently allowed to proceed.
  // start() claims a fresh id on entry; stop()/cancel() bump it when
  // called pre-LISTENING to invalidate any in-flight start whose
  // mic-permission await is still parked.
  pendingStartId: 0,
  dictationId: null,
  dictationRecordingId: null,
  recordingEndedAt: null,
  // SCR-4525: Rust-driven; true between dictation:start (recordingStarted=true)
  // and dictation:result, regardless of trigger source. Used by `DictateButton`
  // to block a click while any native dictation is already in flight.
  nativeDictationActive: false,
} as const;

export const createDictationSlice: StateCreator<
  DictationStore,
  [],
  [],
  DictationSlice
> = (set, get) => ({
  ...initialState,

  reset: () => {
    if (get().status === 'FEEDBACK' && isDictateUserFeedbackEnabled()) {
      getDefaultStore().set(dictationFeedbackPromptCooldownAtom);
    }
    set(initialState);
    get().resetRecording();
  },

  start: async () => {
    const jotaiStore = getDefaultStore();

    const currentStatus = get().status;
    if (
      currentStatus !== 'INACTIVE' &&
      currentStatus !== 'ERROR' &&
      currentStatus !== 'INSERTION_ERROR' &&
      // A lingering post-success feedback prompt must not block a new dictation.
      currentStatus !== 'FEEDBACK'
    ) {
      dictationLogger.debug({
        event_type: 'DICTATION_START_SKIPPED',
        details: { status: currentStatus, skip_reason: 'already_active' },
      });
      return;
    }

    if (currentStatus === 'FEEDBACK' && isDictateUserFeedbackEnabled()) {
      jotaiStore.set(dictationFeedbackPromptCooldownAtom);
    }

    if (!isTauri()) {
      // Fire-and-forget warm-up so glossary fetch overlaps recording, not the
      // stop -> transcribe critical path.
      void resolveDictationContext();
    }

    // SCR-3855: claim a fresh id up-front so a stop()/cancel() that
    // fires while we're awaiting mic permission can invalidate this
    // call by bumping the id past myPendingStartId.
    const myPendingStartId = get().pendingStartId + 1;
    // SCR-4118: mint correlation IDs up-front so any log line (including
    // mic-permission denial paths below) carries them. The chunk pipeline
    // reads these off the store rather than minting its own.
    set({
      pendingStartId: myPendingStartId,
      dictationId: crypto.randomUUID(),
      dictationRecordingId: crypto.randomUUID(),
      // Clear any stop-intent timestamp left over from a prior session that
      // exited without going through reset() (e.g. an ERROR transition).
      recordingEndedAt: null,
    });
    const { setErrorCode } = get();
    const handleDeviceInUseError = (err: unknown): boolean => {
      if (!(err instanceof DeviceInUseError)) return false;
      setErrorCode('ERROR', 'MIC_PERMISSION_DENIED', false, {
        event_type: 'DICTATION_MIC_DEVICE_IN_USE',
        details: { errorName: err instanceof Error ? err.name : typeof err },
      });
      return true;
    };

    try {
      await checkMicPermissionAndDevices();
    } catch (err) {
      if (err instanceof MicPermissionError) {
        setErrorCode('ERROR', 'MIC_PERMISSION_DENIED', false, {
          event_type: 'DICTATION_MIC_PERMISSION_DENIED',
          details: { errorName: err instanceof Error ? err.name : typeof err },
        });
        return;
      }
      if (err instanceof NoMicDevicesError) {
        setErrorCode('ERROR', 'NO_MIC_DEVICES', false, {
          event_type: 'DICTATION_NO_MIC_DEVICES',
          details: { errorName: err instanceof Error ? err.name : typeof err },
        });
        return;
      }
      if (handleDeviceInUseError(err)) return;
      throw err;
    }

    // SCR-3855: bail if stop()/cancel()/reset() fired (or a newer start
    // superseded us) while we were awaiting mic permission. Without this,
    // the bubble flips to LISTENING after the user has already released
    // the hotkey, and the hold-mode UI has no visible stop button.
    if (get().pendingStartId !== myPendingStartId) {
      dictationLogger.debug({
        event_type: 'DICTATION_START_CANCELLED_PRE_LISTENING',
      });
      return;
    }

    const isWindowActive = (): boolean =>
      document.visibilityState === 'visible' && document.hasFocus();

    // Abort if user switched away during mic permission check.
    // Desktop: Skip this check - dictation is triggered via global Fn hotkey while user is in another app.
    if (
      !isWindowActive() &&
      !(isTauri() && getDictationBubblePorts().isDictationEnabled())
    )
      return;

    // Enable audio level monitoring for the Bars component
    getDictationBubblePorts().setReadyToRequestMicPermissions();

    const startedAt = new Date();
    set({
      status: 'LISTENING',
      text: '',
      startedAt,
      messageLevel: null,
      errorCode: null,
      insertionNodes: {
        start: captureSelectionState(),
        stop: null,
      },
    });

    try {
      await get().startRecording();
      get().startSound();

      if (get().dictationMode) {
        trackDictationStarted({
          triggered_by: resolveTriggeredBy(get().dictationMode, get().source),
          dictation_id: get().dictationId,
        });
      }

      const csatState = jotaiStore.get(dictationCsatPopoverStateAtom);
      if (csatState.isOpen) {
        trackDictationCsatDismissed({
          platform: isTauri() ? 'desktop' : 'web',
        });
        jotaiStore.set(dictationCsatDismissAtom);
      }
    } catch (err: unknown) {
      if (handleDeviceInUseError(err)) return;
      get().stopRecording();
      setErrorCode('ERROR', 'FAILED_TO_GET_AUDIO_STREAM', false, {
        event_type: 'DICTATION_START_FAILED',
        details: { errorName: err instanceof Error ? err.name : typeof err },
      });
    }
  },

  stop: async () => {
    const {
      startedAt,
      status,
      setErrorCode,
      insertTranscript,
      insertionNodes,
      dictationMode,
      source,
      dictationId,
    } = get();

    // Only proceed if currently LISTENING - also prevents concurrent stop() calls
    if (status !== 'LISTENING') {
      // SCR-3855: start() may still be awaiting mic permission. Bump
      // the id so the in-flight start bails when its await resolves
      // rather than transitioning to LISTENING after the user has
      // already requested stop.
      set({ pendingStartId: get().pendingStartId + 1 });
      dictationLogger.debug({
        event_type: 'DICTATION_STOP_SKIPPED',
        details: { status, skip_reason: 'not_listening' },
      });
      return false;
    }
    if (!startedAt) {
      dictationLogger.warn({
        event_type: 'DICTATION_STOP_MISSING_STARTED_AT',
        details: { status },
      });
      return false;
    }

    // Set PROCESSING immediately to block concurrent calls. Capture the
    // stop-intent timestamp here (after the LISTENING guard but before any
    // async work) so output_latency_ms reflects the post-key-up wait.
    set({ status: 'PROCESSING', recordingEndedAt: Date.now() });

    // Capture selection state immediately when stop is called,
    // before any async processing.
    const selectionStateAtStop = captureSelectionState();
    set({
      insertionNodes: {
        ...insertionNodes,
        stop: selectionStateAtStop,
      },
    });

    const startedAtDate = new Date(startedAt);
    const holdDurationMs = Date.now() - startedAtDate.getTime();
    const dictationLogDetails = {
      recording_duration_ms: holdDurationMs,
      dictation_mode: dictationMode,
    };
    get().stopRecording();

    if (startedAtDate && holdDurationMs < MIN_RECORDING_DURATION_MS) {
      setErrorCode('INFO', 'PRESS_AND_HOLD', false, {
        event_type: 'DICTATION_RECORDING_TOO_SHORT',
        details: dictationLogDetails,
      });
      return false;
    }

    get().stopSound();

    try {
      await pRetry(
        () => {
          if (get().chunkRecorder?.hasChunksInLimbo()) throw new Error();
        },
        { minTimeout: 200, maxTimeout: 1000, retries: 10, factor: 1.2 }
      );
    } catch {
      dictationLogger.warn({ event_type: 'DICTATION_CHUNKS_STUCK_IN_LIMBO' });
    }

    try {
      await pRetry(
        () => {
          if (get().numChunksProcessing > 0) throw new Error();
        },
        { minTimeout: 200, maxTimeout: 1000, retries: 12, factor: 1.2 }
      );
    } catch {
      dictationLogger.warn({ event_type: 'DICTATION_SOME_CHUNKS_FAILED_TO_PROCESS' });
    }

    // Join transcribed chunks in order by index (all but the pending last chunk)
    const {
      transcribedChunks,
      pendingLastChunk,
      numChunks,
      numChunksProcessing,
      hasReceivedTranscript,
      pendingLastChunkEnabled,
    } = get();
    const orderedText = Object.keys(transcribedChunks)
      .map(Number)
      .sort((a, b) => a - b)
      .map((index) => transcribedChunks[index])
      .join('\n');

    if (!pendingLastChunk) {
      setErrorCode('ERROR', 'COULD_NOT_CONNECT_TO_HEIDI', false, {
        event_type: 'DICTATION_PENDING_LAST_CHUNK_MISSING',
        details: {
          ...dictationLogDetails,
          num_chunks: numChunks,
          num_chunks_processing: numChunksProcessing,
          has_received_transcript: hasReceivedTranscript,
          pending_last_chunk_enabled: pendingLastChunkEnabled,
          transcribed_chunk_count: Object.keys(transcribedChunks).length,
        },
      });
      return false;
    }

    // Door read: the workspace seam's client is the app singleton, so this is
    // the same cache read without the moving cone holding the client.
    const user = getCurrentUser();
    const inputLanguages = readDefaultDictationInputLanguages();

    // Load snippets before buildUserContext reads them from cache.
    await ensureCurrentUserSnippets();
    await ensureCurrentCustomVocabulary();

    const baseUserContext = buildUserContext(user);
    const dictationContext = readDictationContextFromCache();
    const userContext = dictationContext
      ? { ...baseUserContext, dictation_context: dictationContext }
      : baseUserContext;

    let result: ProcessedResult;
    try {
      result = await transcribeChunk(
        pendingLastChunk.chunk,
        pendingLastChunk.format,
        inputLanguages,
        pendingLastChunk.useLogsBackend,
        {
          isLastChunk: true,
          lastChunkContext: {
            accumulatedText: orderedText,
            userContext,
            // Default true matches the backend.
            verbalCommandsEnabled: user?.dictation?.verbal_commands_enabled ?? true,
          },
        }
      );
    } catch (err) {
      // PHI-safe diagnostics only (codes/status/online flag) — never the raw
      // error, which can carry request/response bodies.
      const failure = describeTranscriptionFailure(err);
      setErrorCode(
        'ERROR',
        failure.failure_class === 'authentication'
          ? 'AUTHENTICATION_REQUIRED'
          : 'COULD_NOT_CONNECT_TO_HEIDI',
        false,
        {
          event_type: 'DICTATION_FINAL_CHUNK_TRANSCRIPTION_FAILED',
          details: {
            ...dictationLogDetails,
            ...failure,
            ordered_text_length: orderedText.length,
            input_language_count: inputLanguages?.length ?? 0,
            chunk_index: pendingLastChunk.chunk.index,
            chunk_duration: pendingLastChunk.chunk.duration,
          },
        }
      );
      return false;
    }

    const postProcessed = result.processed?.trim() || '';
    const postProcessingModel = result.post_processing_model;
    const transcript = [orderedText, result.text].filter(Boolean).join('\n').trim();

    // Abort if cancelled or error occurred during async processing
    const currentStatus = get().status;
    if (currentStatus !== 'PROCESSING') {
      dictationLogger.debug({
        event_type: 'DICTATION_PROCESSING_ABORTED',
        details: {
          ...dictationLogDetails,
          status: currentStatus,
          skip_reason: 'status_changed_during_processing',
        },
      });
      return false;
    }

    if (transcript.length < 1 && holdDurationMs >= 5000) {
      setErrorCode('ERROR', 'COULD_NOT_HEAR_LONG', true, {
        event_type: 'DICTATION_EMPTY_TRANSCRIPTION',
        details: {
          ...dictationLogDetails,
          text_length: transcript.length,
        },
      });
      return false;
    }

    set({ text: postProcessed });

    // Snapshot recordingEndedAt before insertTranscript runs — it calls
    // reset() on success, which clears the field before the fire site below.
    const recordingEndedAtSnapshot = get().recordingEndedAt;

    const didInsert = insertTranscript({
      text: postProcessed,
      selectionState: selectionStateAtStop,
      dictationLogDetails,
    });

    if (!didInsert) {
      return false;
    }

    // DEV-12122: a successful dictation produces a new logs row server-side,
    // so the infinite query cache is stale until refetched. Without this, the
    // dictation logs spotlight (which gates on `logs.length > 0`) wouldn't
    // see the newly-created first log for users who hadn't dictated before.
    invalidateDictationLogs();

    // Fire completed only on a genuinely successful outcome (transcription +
    // insertion both succeeded). Earlier revisions fired this on key release,
    // which double-counted error attempts in the started→completed funnel.
    if (dictationMode) {
      // Use the captured `source` from the top of stop() — insertTranscript
      // calls reset() on success, which clears get().source before this fires.
      const triggeredBy = resolveTriggeredBy(dictationMode, source);
      trackDictationCompleted({
        hold_duration_ms: holdDurationMs,
        triggered_by: triggeredBy,
        word_count: postProcessed.trim().split(/\s+/).filter(Boolean).length,
        hotkey: formatHotkeyForAnalytics(triggeredBy),
        output_latency_ms: computeOutputLatencyMs(recordingEndedAtSnapshot),
        dictation_id: dictationId,
        outcome: 'success',
      });
    }

    dictationLogger.info({
      event_type: 'DICTATION_SUCCESS',
      details: {
        ...dictationLogDetails,
        text_length: postProcessed.length,
        post_processing_model: postProcessingModel ?? null,
      },
    });
    // In-app JS dictation only (OS hotkey uses the native widget path).
    armDictateReengagementCard('inline');
    return postProcessed.length > 0;
  },

  cancel: () => {
    // SCR-3855: also invalidate any in-flight start() — Escape during
    // the permission popup is the same race as the blur-triggered stop.
    set({ pendingStartId: get().pendingStartId + 1 });
    const { startedAt, setErrorCode } = get();
    if (!startedAt) return;
    get().stopRecording();
    // SCR-4118: null the correlation IDs so any log fired between now and
    // the next start() (config-sync error, sound-load failure, etc.) is
    // not misattributed to the cancelled session.
    set({
      status: 'INACTIVE',
      startedAt: null,
      dictationId: null,
      dictationRecordingId: null,
      recordingEndedAt: null,
    });
    setErrorCode('INFO', 'CANCELLED');
  },

  setErrorCode: (
    level: MessageLevel,
    errorCode: ErrorCode,
    persistent = false,
    logContext
  ) => {
    const { errorSound, stopRecording, startedAt, recordingEndedAt } = get();
    const holdDurationMs = startedAt
      ? (recordingEndedAt ?? Date.now()) - new Date(startedAt).getTime()
      : 0;

    if (get().status === 'LISTENING' || get().status === 'PROCESSING') {
      stopRecording();
    }

    // Clear the stop-intent timestamp so a later session's completed event
    // can't pick up a stale value (e.g. recording-too-short / empty-transcript
    // early-exits from stop() leave the slice in ERROR without going through
    // reset()). Out of scope for any analytics today (the completed event only
    // fires on success), but keeps the slice invariant honest.
    set({ recordingEndedAt: null });

    if (logContext) {
      const payload = {
        event_type: logContext.event_type,
        details: {
          level,
          error_code: errorCode,
          persistent,
          ...(logContext.details ?? {}),
        },
      };

      if (level === 'ERROR') {
        dictationLogger.error(payload);
      } else if (level === 'WARN') {
        dictationLogger.warn(payload);
      } else {
        dictationLogger.info(payload);
      }
    }

    set({
      status: 'ERROR',
      messageLevel: level,
      errorCode,
      persistent,
    });
    if (level === 'ERROR' || level === 'WARN') {
      errorSound();
    }

    // Fire session-end analytics for every non-cancelled outcome. API failures
    // emit universal_dictation_errored; everything else emits
    // universal_dictation_completed with an outcome (could_not_hear_long,
    // no_input_field_selected, etc.).
    if (errorCode !== 'CANCELLED' && get().dictationMode) {
      const { dictationMode, source, text, dictationId } = get();
      const triggeredBy = resolveTriggeredBy(dictationMode, source);
      // A response-class failure (4xx/5xx) reports its status: axios collapses
      // every one of those into ERR_BAD_REQUEST / ERR_BAD_RESPONSE, so the code
      // is only informative when the request never got a response.
      const details = logContext?.details;
      const underlyingErrorCode =
        typeof details?.http_status === 'number'
          ? `HTTP_${details.http_status}`
          : typeof details?.underlying_error_code === 'string'
            ? details.underlying_error_code
            : undefined;
      trackDictationSessionEnd(errorCode, {
        triggered_by: triggeredBy,
        hold_duration_ms: holdDurationMs,
        word_count: text ? text.trim().split(/\s+/).filter(Boolean).length : 0,
        dictation_id: dictationId,
        hotkey: formatHotkeyForAnalytics(triggeredBy),
        output_latency_ms: null,
        ...(underlyingErrorCode ? { underlying_error_code: underlyingErrorCode } : {}),
      });
    }
  },

  setDictationMode: (mode) => {
    set({ dictationMode: mode });
  },

  setEditor: (editor) => {
    set({ editor });
  },

  setSource: (source) => {
    set({ source });
  },

  setDictationIds: ({ dictationId, dictationRecordingId }) => {
    set({ dictationId, dictationRecordingId });
  },

  clearDictationIds: () => {
    set({ dictationId: null, dictationRecordingId: null });
  },

  setNativeDictationActive: (active) => {
    set({ nativeDictationActive: active });
  },

  insertTranscript: ({
    text,
    selectionState,
    dictationLogDetails,
  }: {
    text: string;
    selectionState?: SelectionState | null;
    dictationLogDetails: Record<string, unknown>;
  }) => {
    const { reset, resetRecording, insertionNodes } = get();

    // DOM-based insertion (web only - desktop uses native Rust insertion)
    try {
      // Use provided selection state, or fall back to stored/current state
      const targetState =
        selectionState ||
        insertionNodes.stop ||
        insertionNodes.start ||
        captureSelectionState();

      // Captured before reset() clears it — the feedback prompt only shows for
      // the sources that render the bubble (webDictateButton stays headless).
      const { source, dictationId } = get();
      insertText(text, targetState);
      reset();

      // Flag on: show the post-success output-feedback prompt in the bubble and
      // suppress the star CSAT so the user never gets two feedback surfaces.
      // Flag off: unchanged — roll the CSAT.
      if (getDictationBubblePorts().isProductFeedbackAvailable()) {
        if (isDictateUserFeedbackEnabled()) {
          if (source !== 'webDictateButton') {
            void getDefaultStore()
              .set(dictationFeedbackPromptIncrementAtom)
              .then((shouldShowFeedback) => {
                // Billing is refreshed for this roll. Do not interrupt a new
                // dictation that started while the request was in flight.
                if (
                  shouldShowFeedback &&
                  get().status === 'INACTIVE' &&
                  get().dictationId === null
                ) {
                  set({ status: 'FEEDBACK', dictationId });
                }
              });
          }
        } else {
          getDefaultStore().set(dictationCsatIncrementAtom);
        }
      }
      return true;
    } catch (err) {
      if (err instanceof DictationInsertionError) {
        dictationLogger.warn({
          event_type: 'DICTATION_INSERTION_FAILED',
          details: {
            ...dictationLogDetails,
            text_length: text.length,
            has_selection_state: Boolean(selectionState),
            has_stop_insertion_node: Boolean(insertionNodes.stop),
            has_start_insertion_node: Boolean(insertionNodes.start),
          },
        });
        const { startedAt, dictationMode, source, dictationId } = get();
        const holdDurationMs = startedAt ? Date.now() - new Date(startedAt).getTime() : 0;
        const wordCount = text ? text.trim().split(/\s+/).filter(Boolean).length : 0;
        const triggeredBy = resolveTriggeredBy(dictationMode, source);
        set({
          status: 'INSERTION_ERROR',
          errorCode: 'NO_INPUT_FIELD_SELECTED',
          messageLevel: 'WARN',
          source: null,
        });
        resetRecording();

        trackDictationSessionEnd('NO_INPUT_FIELD_SELECTED', {
          triggered_by: triggeredBy,
          hold_duration_ms: holdDurationMs,
          word_count: wordCount,
          dictation_id: dictationId,
          hotkey: formatHotkeyForAnalytics(triggeredBy),
          output_latency_ms: null,
        });
        if (
          getDictationBubblePorts().isProductFeedbackAvailable() &&
          !isDictateUserFeedbackEnabled()
        ) {
          getDefaultStore().set(dictationCsatIncrementAtom);
        }
        return false;
      }

      throw err;
    }
  },
});
